<template>
  <el-cascader
    ref="divCascader"
    v-model="selectedValue"
    :options="cascaderOptions"
    :props="finalCascaderProps"
    @visible-change="onVisibleChange"
    @change="handleDivChange"
    placeholder="請選擇區處分行"
    :style="`width: ${width}`"
    clearable
    :show-all-levels="true"
  />
</template>

<script>
import { handleError } from "../utils/notify";

export default {
  name: "DivSelection",

  props: {
    value: {
      type: Array,
      default: () => [],
    },
    width: {
      type: String,
      default: "200px",
    },
    filterDivisionCode: {
      type: String,
      default: null, // 例如：'H91'，為 null 時表示不篩選
    },
    noDivision: {
      type: Boolean,
      default: false, // 當為 true 時，過濾掉DeptID 991-996的分行，且不將最後一筆提到最前面
    },
  },

  data() {
    return {
      selectedValue: this.value,
      divisionList: [
        { DivisionCode: "991", DivisionName: "北一區" },
        // { DivisionCode: "992", DivisionName: "北二區" },
        // { DivisionCode: "993", DivisionName: "北三區" },
        // { DivisionCode: "994", DivisionName: "中區" },
        // { DivisionCode: "995", DivisionName: "南一區" },
        { DivisionCode: "996", DivisionName: "南二區" },
      ],
      cascaderProps: {
        value: "value",
        label: "label",
        children: "children",
        lazy: true,
        lazyLoad: this.lazyLoadBranch,
        expandTrigger: "click",
        emitPath: true, // 回傳完整路徑陣列 [區處代碼, 分行代碼]
      },
    };
  },

  computed: {
    finalCascaderProps() {
      const isMobileOrTablet = typeof window !== "undefined" && /iPad|iPhone|iPod|android|Macintosh/i.test(navigator.userAgent) && "ontouchend" in document;

      return {
        ...this.cascaderProps,
        expandTrigger: isMobileOrTablet ? "hover" : "click",
      };
    },

    cascaderOptions() {
      // 根據 filterDivisionCode 篩選區處列表
      let filteredDivisions = this.divisionList;

      if (this.filterDivisionCode) {
        // 只保留指定的區域
        filteredDivisions = this.divisionList.filter((division) => division.DivisionCode === this.filterDivisionCode);
      }

      // 將區處列表轉換為 cascader 選項
      return filteredDivisions.map((division) => ({
        value: division.DivisionCode,
        label: division.DivisionName,
        leaf: false, // 標記為非葉節點，表示還有下一級
      }));
    },
  },

  watch: {
    value(newVal) {
      if (JSON.stringify(this.selectedValue) !== JSON.stringify(newVal)) {
        this.selectedValue = newVal;
      }
    },
    selectedValue: {
      handler(newVal) {
        if (JSON.stringify(this.value) !== JSON.stringify(newVal)) {
          this.$emit("input", newVal);
        }
      },
      deep: true,
    },
  },

  mounted() {
    // 基本初始化
  },

  beforeDestroy() {
    // 清理工作
  },

  methods: {
    onVisibleChange(visible) {
      const isMobileOrTablet = typeof window !== "undefined" && /iPad|iPhone|iPod|android|Macintosh/i.test(navigator.userAgent) && "ontouchend" in document;

      if (visible && isMobileOrTablet) {
        const bindLeafNodes = () => {
          this.$nextTick(() => {
            const nodes = document.querySelectorAll(".el-cascader-node");

            nodes.forEach((node) => {
              const isLeaf = node.classList.contains("is-selectable") || !node.querySelector(".el-icon-arrow-right");

              if (isLeaf && !node.classList.contains("has-touch-bound")) {
                node.classList.add("has-touch-bound");

                node.addEventListener(
                  "touchend",
                  (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    node.click();
                  },
                  { passive: false },
                );
              }
            });
          });
        };

        bindLeafNodes();

        // 💡 【修正此處】：改成當前元件的 ref 名稱 divCascader
        const popper = this.$refs.divCascader?.$refs?.popper;
        const menuEl = popper || document.querySelector(".el-cascader__dropdown");

        if (menuEl && !menuEl.classList.contains("has-lazy-bound")) {
          menuEl.classList.add("has-lazy-bound");

          menuEl.addEventListener("touchend", () => {
            setTimeout(bindLeafNodes, 250);
          });
        }
      }
    },

    // 懶加載分行資料
    async lazyLoadBranch(node, resolve) {
      console.log("懶加載節點：", node);

      // 第一層（區處）展開時，加載該區處下的分行
      if (node.level === 1) {
        const divisionCode = node.value;

        this.$store.dispatch("loadingHandler", true);

        try {
          const qry = {
            Page_Num: 1,
            Page_Size: 100,
            DivisionCode: divisionCode,
          };

          console.log("查詢分行參數：", qry);

          const res = await this.$api.apidivison.division_info_list(qry);
          console.log("獲取的分行資料：", res.data);
          let branches = res.data || [];

          // 當 noDivision 為 true 時，過濾掉 DeptID 為 991-996 的分行
          if (this.noDivision) {
            branches = branches.filter((branch) => !["991", "992", "993", "994", "995", "996"].includes(branch.DeptID));
          } else {
            // 把最後一筆, 提前到最前面, 因為最後一筆是指區處總行
            if (branches.length > 1) {
              const lastBranch = branches.pop();
              branches.unshift(lastBranch);
            }
          }
          console.log("獲取的分行資料：", branches);

          // 將分行資料轉換為選項格式
          const branchOptions = branches.map((branch) => ({
            value: branch.DeptID,
            label: branch.DeptName,
            leaf: true, // 標記為葉節點，沒有下一級了
          }));

          resolve(branchOptions);
        } catch (error) {
          handleError(error, "獲取分行資料失敗");
          resolve([]);
        } finally {
          this.$store.dispatch("loadingHandler", false);
        }
      } else {
        resolve([]);
      }
    },

    // 處理選擇變更
    handleDivChange(values) {
      // console.log('DivSelection - 選擇的區處分行（完整路徑）：', values);

      // 發送變更事件給父組件
      // values[0] 是區處代碼，values[1] 是分行代碼
      this.$emit("div-change", values);

      // 如果需要，可以額外發送分行資訊
      if (values && values.length === 2) {
        const divisionCode = values[0];
        const DeptID = values[1];

        // 找到對應的區處名稱
        const division = this.divisionList.find((d) => d.DivisionCode === divisionCode);

        this.$emit("branch-selected", {
          DivisionCode: divisionCode,
          DivisionName: division ? division.DivisionName : "",
          DeptID: DeptID,
        });
      }
    },
  },
};
</script>

<style scoped>
/* 基本樣式，讓 cascader 看起來美觀 */
::v-deep .el-cascader {
  width: 100%;
  border-radius: 4px;
}

::v-deep .el-cascader:hover {
  border-color: #409eff;
}

::v-deep .el-cascader-menu {
  min-width: 180px;
}

::v-deep .el-cascader-node {
  padding: 10px 20px;
}

::v-deep .el-cascader-node.is-active {
  color: #409eff;
  font-weight: bold;
}

::v-deep .el-cascader-node:hover {
  background-color: #f5f7fa;
}

::v-deep .el-cascader-node,
::v-deep .el-cascader-menu__list {
  touch-action: manipulation !important;
  -webkit-tap-highlight-color: transparent;
}

::v-deep .el-cascader-node.is-selectable {
  touch-action: manipulation !important;
}

::v-deep .el-cascader-node__label {
  pointer-events: auto !important;
  width: 100%;
  height: 100%;
  display: block;
}
</style>
